import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { BusinessUserApiService } from '@api-services/business-user-api.service';
import { PartnerConfigApiService } from '@api-services/partner-config-api.service';
import { AppStoreState } from '@interfaces/app-store-state';
import { BusinessUser } from '@models/business-user';
import { PartnerConfig } from '@models/partner-config';
import { BeamerService } from '@third-parties/beamer/beamer.service';
import { SentryService } from '@third-parties/sentry/sentry.service';

import { reload, select } from './business-user-actions';
import { businessUserSelector } from './business-user-selector';

@Injectable()
export class BusinessUserEffects {
  reload$ = createEffect(() =>
    this.actions$.pipe(
      ofType(reload),
      switchMap(() =>
        this.businessUserApiService.getBusinessUser().pipe(
          switchMap((businessUser: BusinessUser) =>
            businessUser.partner?.id
              ? this.partnerConfigApiService.getConfig(businessUser.partner.id).pipe(
                  map((config: PartnerConfig) => {
                    businessUser.partner.config = config;
                    return businessUser;
                  }),
                  catchError(() => of(businessUser))
                )
              : of(businessUser)
          ),
          map((businessUser: BusinessUser) => select({ businessUser })),
          catchError(() => of(select({ businessUser: null })))
        )
      )
    )
  );

  updateThirdParties$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(select),
        withLatestFrom(this.store.select(businessUserSelector)),
        tap(([, businessUser]: [any, BusinessUser]) => this.sentryService.setUserFromBusinessUser(businessUser)),
        tap(([, businessUser]: [any, BusinessUser]) => this.beamerService.update(businessUser))
      ),
    { dispatch: false }
  );

  constructor(
    private readonly store: Store<AppStoreState>,
    private readonly actions$: Actions,
    private readonly sentryService: SentryService,
    private readonly beamerService: BeamerService,
    private readonly businessUserApiService: BusinessUserApiService,
    private readonly partnerConfigApiService: PartnerConfigApiService
  ) {}
}
